import {useAuth} from '@/components/AuthProvider';
import {ClubsSidebar} from '@/components/clubsSidebar/clubsSidebar';
import {EventListCard} from '@/components/eventListCard/eventListCard';
import {GroupedEventList} from '@/components/groupedEventList/groupedEventList';
import {ProfileHeader} from '@/components/profileHeader/profileHeader';
import {Button} from '@/components/ui/button';
import {Footer} from '@/components/ui/footer';
import {Navbar} from '@/components/ui/navbar';
import {toastManager} from '@/components/ui/toast';
import {Typography} from '@/components/ui/typography';
import {getPublicProfile, getPublicProfileEvents} from '@/server/profile';
import {followProfile, unfollowProfile} from '@/server/profileFollower';
import {getProfileName} from '@/utils/getProfileName';
import {
  Link,
  createFileRoute,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import {useCallback, useEffect, useRef, useState} from 'react';
import {z} from 'zod';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://wayra.cc';

const profileSearchSchema = z.object({
  follow: z.coerce.boolean().optional().catch(undefined),
});

export const Route = createFileRoute('/rider/$profileId/')({
  validateSearch: (search) => profileSearchSchema.parse(search),
  loader: async ({params}) => {
    const [profile, events] = await Promise.all([
      getPublicProfile({data: {profileId: params.profileId}}),
      getPublicProfileEvents({data: {profileId: params.profileId}}),
    ]);
    return profile ? {...profile, events} : null;
  },
  head: ({params, loaderData}) => {
    if (!loaderData) return {meta: [{title: 'Profile not found · Wayra'}]};
    const name = getProfileName(loaderData.profile);
    const canonicalUrl = `${SITE_URL}/rider/${params.profileId}`;
    return {
      meta: [
        {title: `${name} · Wayra`},
        {
          name: 'description',
          content: `${name}'s public lists on Wayra.`,
        },
      ],
      links: [
        {rel: 'canonical', href: canonicalUrl},
      ],
    };
  },
  errorComponent: PublicProfileNotFound,
  pendingComponent: PublicProfilePending,
  component: PublicProfilePage,
});

function PublicProfilePage() {
  const data = Route.useLoaderData();
  const {follow} = Route.useSearch();
  const router = useRouter();
  const navigate = useNavigate();
  const {profile: viewer} = useAuth();
  const autoFollowHandled = useRef(false);

  const serverIsFollowing = data?.social.isFollowing ?? false;
  const [isFollowing, setIsFollowing] = useState(serverIsFollowing);
  const [followBusy, setFollowBusy] = useState(false);
  const inflight = useRef(false);

  useEffect(() => {
    if (!inflight.current) setIsFollowing(serverIsFollowing);
  }, [serverIsFollowing]);

  const targetProfileId = data?.profile.id ?? null;

  const runFollow = useCallback(
    async (next: boolean) => {
      if (!targetProfileId || inflight.current) return;
      inflight.current = true;
      setFollowBusy(true);
      const previous = isFollowing;
      setIsFollowing(next);

      try {
        if (next) {
          await followProfile({data: {profileId: targetProfileId}});
          toastManager.add({title: 'Following', type: 'success'});
        } else {
          await unfollowProfile({data: {profileId: targetProfileId}});
          toastManager.add({title: 'Unfollowed', type: 'success'});
        }
        router.invalidate();
      } catch (err) {
        setIsFollowing(previous);
        toastManager.add({
          title: err instanceof Error ? err.message : 'Failed to update follow',
          type: 'error',
        });
      } finally {
        inflight.current = false;
        setFollowBusy(false);
      }
    },
    [targetProfileId, isFollowing, router]
  );

  const handleToggleFollow = useCallback(() => {
    if (!viewer) {
      if (!targetProfileId) return;
      navigate({
        to: '/login',
        search: {redirect: `/rider/${targetProfileId}?follow=true`},
      });
      return;
    }
    runFollow(!isFollowing);
  }, [viewer, targetProfileId, isFollowing, navigate, runFollow]);

  useEffect(() => {
    if (!follow || autoFollowHandled.current) return;
    autoFollowHandled.current = true;

    const url = new URL(window.location.href);
    url.searchParams.delete('follow');
    history.replaceState(null, '', url.pathname + url.search);

    if (!data || data.social.isOwner || data.social.isFollowing) return;
    runFollow(true);
  }, [follow, data, runFollow]);

  if (!data) return <PublicProfileNotFound />;

  const {profile, publicLists, publicListsCount, social, events, memberships} =
    data;
  const headerSocial = {...social, isFollowing};

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      <div className="max-w-5xl mx-auto w-full px-4 py-8">
        <ProfileHeader
          profile={profile}
          social={headerSocial}
          followBusy={followBusy}
          onToggleFollow={handleToggleFollow}
        />
      </div>
      <hr />
      <main className="max-w-5xl mx-auto w-full px-4 flex-1 pb-8">
        <div className="flex flex-col lg:flex-row lg:gap-8">
          <div className="lg:order-2 lg:w-[280px] lg:shrink-0">
            <ClubsSidebar memberships={memberships} />
          </div>
          <div className="flex flex-col gap-8 flex-1 lg:order-1 lg:border-r lg:pr-8 pt-8">
            <section className="flex flex-col gap-4">
              <header className="flex items-baseline justify-between gap-3">
                <Typography level="H4" className="italic font-semibold">
                  Lists
                </Typography>
                {publicListsCount > publicLists.length ? (
                  <Link
                    to="/rider/$profileId/lists"
                    params={{profileId: profile.id}}
                    className="text-sm hover:underline whitespace-nowrap"
                  >
                    See all {publicListsCount} lists →
                  </Link>
                ) : null}
              </header>
              {publicListsCount === 0 ? (
                <Typography level="B1" className="text-neutral-500">
                  No public lists yet.
                </Typography>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
                  {publicLists.map((list) => (
                    <EventListCard
                      key={list.id}
                      list={list}
                      profileId={profile.id}
                    />
                  ))}
                </div>
              )}
            </section>
            {events.length > 0 ? (
              <section className="flex flex-col gap-4">
                <Typography level="H4" className="italic font-semibold">
                  Upcoming Events
                </Typography>
                <GroupedEventList events={events} />
              </section>
            ) : null}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function PublicProfilePending() {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      <div
        className="mx-auto flex w-full max-w-5xl flex-1 flex-col gap-6 px-4 py-8"
        aria-busy="true"
      >
        <div className="flex items-center gap-4">
          <div className="h-20 w-20 animate-pulse rounded-full bg-neutral-200" />
          <div className="flex flex-1 flex-col gap-2">
            <div className="h-8 w-48 animate-pulse rounded bg-neutral-200" />
            <div className="h-4 w-32 animate-pulse rounded bg-neutral-200" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({length: 4}).map((_, i) => (
            <div
              key={i}
              className="h-48 animate-pulse rounded-lg bg-neutral-200"
            />
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}

function PublicProfileNotFound() {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      <div className="flex flex-1 flex-col items-center justify-center gap-4 py-12">
        <Typography level="H3">Profile not found</Typography>
        <Typography level="B1" className="text-neutral-500">
          This profile doesn&apos;t exist.
        </Typography>
        <Link to="/">
          <Button variant="outline">Back to Wayra</Button>
        </Link>
      </div>
      <Footer />
    </div>
  );
}
